import { get, sumBy } from 'lodash/fp';

export function assessLimits(games = [], quota = Infinity) {
  /*
   * A short explanation of these variables:
   * remainingTotalSelections: the difference between the total so far selected and the total
   * required.
   * remainingFreeSelections: the remaining selections that are not reserved for particular
   * positions.
   * For instance suppose I have BAT 4 (of 4-6) BOWL 4 (of 4-6) WK 0 (or 1) for a total of 8 (of 11)
   * Then I have three remaining selections, but one of these is allocated as a WK selection,
   * leaving only two "free" selections
   */

  const totalMaximum = quota;
  const totalSelected = sumBy('selected')(games);
  const totalAllocated = sumBy(game => Math.max(game.min, game.selected))(games);

  const remainingTotalSelections = totalMaximum - totalSelected;
  const remainingFreeSelections = Math.max(0, totalMaximum - totalAllocated);

  /*
   * A game is considered "filled" if:
   * a) The number of selections meets the minimum and there are no more free selections; or
   * b) The number of selections is at least as many as the maximum
   *
   * A game is in error if:
   * a) There are more selections than the maximum; or
   * b) There are fewer selections than the minimum and insufficient remaining selections to make
   * up the difference
   */

  return games.map(game => ({
    ...game,
    filled:
      (remainingFreeSelections === 0 && game.selected >= game.min) || game.selected >= game.max,
    hasError: game.selected > game.max || game.min - game.selected > remainingTotalSelections
  }));
}

export const getGames = (games = [], playersPerGame = {}, selectionsByGame) =>
  Object.values(games).map(game => {
    const selected = get(game.id, selectionsByGame) || 0;
    return {
      ...game,
      ...playersPerGame,
      selected,
      label: `L${game.gameOrder}`
    };
  });
